import { ColDef } from 'ag-grid-community';
import { useTranslation } from 'react-i18next';

import { CONTEXT_MENU_CELL_ID } from '@/shared/components/ui/data-table/components/ag-grid-table-core/ag-grid-table-core.constants';
import { getDateComparator } from '@/shared/components/ui/data-table/data-table.comparator';
import { useGroupsConfig } from '@/shared/features/groups/context/groups-config-context';
import { TGroupListActionsRenderer } from '@/shared/features/groups/context/groups-config-context.types';
import { useGroupColumn } from '@/shared/features/groups/hooks/use-group-column/use-group-column';
import { usePermissionCheck } from '@/shared/features/permissions/hooks/use-permission-check/use-permission-check';
import {
  RoleActionsRenderer,
  RoleNameRenderer,
  RoleSsoGroupMappingRenderer,
  RoleUsedByRenderer,
} from '@/shared/features/rbac/components/roles-list/roles-list.renderer';
import { useRbacConfig } from '@/shared/features/rbac/context/rbac-config-context';
import { TTransformedRole } from '@/shared/features/rbac/hooks/use-roles-transformer/use-roles-transformer.types';

// Constants for column widths
const COLUMN_WIDTHS = {
  name: { min: 300, default: 300 },
  roleType: { min: 150 },
  creationDate: { min: 150 },
  usedBy: { min: 150 },
  group: { min: 200 },
  ssoGroupMapping: { min: 180 },
  actions: { fixed: 75 },
} as const;

// Base column configuration factory
const createBaseColumns = (t: (key: string) => string) => ({
  name: (): ColDef => ({
    field: 'name',
    headerName: t('rbac:role_name'),
    minWidth: COLUMN_WIDTHS.name.min,
    width: COLUMN_WIDTHS.name.default,
    cellRenderer: RoleNameRenderer,
    sortable: true,
  }),
  roleType: (): ColDef => ({
    field: 'roleType',
    headerName: t('rbac:type'),
    minWidth: COLUMN_WIDTHS.roleType.min,
    sortable: true,
  }),
  creationDate: (): ColDef => ({
    field: 'creationDate',
    headerName: t('rbac:creation_date'),
    minWidth: COLUMN_WIDTHS.creationDate.min,
    sortable: true,
    comparator: getDateComparator<TTransformedRole>(
      (data) => data.creationTimestamp,
    ),
  }),
  usedBy: (): ColDef => ({
    field: 'userTags',
    headerName: t('rbac:used_by'),
    cellRenderer: RoleUsedByRenderer,
    minWidth: COLUMN_WIDTHS.usedBy.min,
    sortable: false,
  }),
});

// Role-specific column definitions
const createRoleColumns = (t: (key: string) => string) => ({
  ssoGroupMapping: (): ColDef => ({
    field: 'ssoGroupTags',
    headerName: t('rbac:sso_group_mapping'),
    cellRenderer: RoleSsoGroupMappingRenderer,
    minWidth: COLUMN_WIDTHS.ssoGroupMapping.min,
    sortable: false,
  }),
  actions: (render: any): ColDef => ({
    field: CONTEXT_MENU_CELL_ID,
    headerName: '',
    cellRenderer: render,
    width: COLUMN_WIDTHS.actions.fixed,
    minWidth: COLUMN_WIDTHS.actions.fixed,
    sortable: false,
    resizable: false,
  }),
});

/**
 * Hook for Roles List column definitions
 * @param ssoEnabled - Whether to include the SSO group mapping column
 */
export const useRolesListDefinitions = (ssoEnabled?: boolean): ColDef[] => {
  const { t } = useTranslation();
  const groupColumn = useGroupColumn();
  const { canAny } = usePermissionCheck();
  const { permissionActions } = useRbacConfig();
  // Drop the actions column entirely when the user can manage no role at all.
  const canManageRoles = canAny([
    permissionActions.updateUserRole,
    permissionActions.deleteUserRole,
  ]);

  const base = createBaseColumns(t);
  const role = createRoleColumns(t);

  const columnDefs: ColDef[] = [
    base.name(),
    base.roleType(),
    base.creationDate(),
    base.usedBy(),
    groupColumn,
  ];

  if (ssoEnabled) {
    columnDefs.push(role.ssoGroupMapping());
  }

  if (canManageRoles) {
    columnDefs.push(role.actions(RoleActionsRenderer));
  }

  return columnDefs;
};

export const useGroupRolesListDefinitions = (options?: {
  ssoEnabled?: boolean;
  actionsRenderer?: TGroupListActionsRenderer;
}): ColDef[] => {
  const { t } = useTranslation();
  const { ssoEnabled, actionsRenderer } = options ?? {};
  const { can } = usePermissionCheck();
  const { permissionActions } = useGroupsConfig();
  // Row actions here move/remove roles between groups → group edit. Drop the
  // column when the user can manage no group at all.
  const canManageGroup = can(permissionActions.updateGroup);

  const base = createBaseColumns(t);
  const role = createRoleColumns(t);

  const columnDefs: ColDef[] = [
    base.name(),
    base.roleType(),
    base.creationDate(),
    base.usedBy(),
  ];

  if (ssoEnabled) {
    columnDefs.push(role.ssoGroupMapping());
  }

  if (actionsRenderer && canManageGroup) {
    columnDefs.push(role.actions(actionsRenderer));
  }

  return columnDefs;
};
